import React from "react";
import { PhoneCall, ShieldAlert, FileCheck2, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  return <section id="hero" data-nav="dark" className="relative min-h-[580px] lg:min-h-[640px] flex items-center justify-center overflow-hidden bg-foreground text-background">
      {/* Background Image with Responsive Attributes */}
      <div className="absolute inset-0 z-0 select-none pointer-events-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_damage_inspection_crew.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_damage_inspection_crew.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/fire_damage_inspection_crew.png-wvc-sizes" alt="Restoration specialist evaluating structural soot and charred interior damage" className="h-full w-full object-cover object-center" loading="eager" decoding="async" />
        {/* Dark Scrim Overlay for Contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/90 via-foreground/80 to-foreground/95" />
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="max-w-3xl">
          {/* Operational Status Pill */}
          <div className="inline-flex items-center gap-2 rounded-full border border-background/20 bg-background/10 backdrop-blur-xs px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-background mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
            </span>
            <span>24/7 Rapid Fire Response • MD / DC / NOVA</span>
          </div>

          {/* H1 Heading */}
          <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-background leading-tight">
            Fire, Smoke & Odor Restoration
          </h1>

          {/* Supporting Line */}
          <p className="mt-4 text-lg sm:text-xl font-medium text-background/90 leading-snug">
            Immediate emergency board-up, soot extraction, and seamless structural reconstruction under one trusted team.
          </p>

          {/* Body Paragraph */}
          <p className="mt-4 text-base sm:text-lg text-background/80 font-normal leading-relaxed max-w-2xl">
            Corrosive soot and toxic smoke penetrate wall cavities, subfloors, and ventilation within hours of containment. Our local technicians dispatch directly to stabilize the property, neutralize odor compounds, and coordinate directly with your insurance adjuster throughout the entire recovery.
          </p>

          {/* Twin Action Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            {/* Green Call Now Button */}
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-6 py-6 text-base rounded-md shadow-md transition-[background-color,box-shadow]">
              <a href="tel:855-435-6631">
                <PhoneCall className="mr-2 size-5" />
                Call Now
              </a>
            </Button>

            {/* Safety-Yellow Request Service Button */}
            <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 border border-accent font-extrabold px-6 py-6 text-base rounded-md shadow-md transition-[background-color,box-shadow]">
              <Link to="/ContactRequestServicePage">
                Request Service
              </Link>
            </Button>
          </div>

          {/* Technical Spec & Credential Badges */}
          <div className="mt-10 pt-6 border-t border-background/15 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-medium text-background/85">
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-accent shrink-0" />
              <span>Direct local crew dispatch (no call centers)</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="size-4 text-accent shrink-0" />
              <span>Full containment & HEPA air scrubbing</span>
            </div>
            <div className="flex items-center gap-2">
              <FileCheck2 className="size-4 text-accent shrink-0" />
              <span>Direct coordination with insurance adjusters</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}